import { useCallback } from 'react';
import { ApiService } from '../services/apiService';
import { OrderItem, LocalSubBill, Order } from '../types';
import { getFloorOrderAttribution } from '../services/floorOrderAttribution';
import { usePinSessions } from '../contexts/PinSessionsContext';

interface ChangeResponse {
  message?: string;
  [key: string]: unknown;
}

export interface POSAPIActions {
  createOrder: (orderData: CreateOrderData) => Promise<Order>;
  processChange: (changeData: ChangeData) => Promise<ChangeResponse>;
}

export interface CreateOrderData {
  totalAmount: number;
  totalTax: number;
  paymentMethod: 'cash' | 'card' | 'split';
  items: OrderItem[];
  subBills?: LocalSubBill[];
  tips?: number;
  cashReceived?: number;
  change?: number;
  notes?: string;
  waiterUserId?: number | null;
  waiterDisplayName?: string | null;
  tableLabel?: string | null;
}

export interface ChangeData {
  amount: number;
  direction: 'card-to-cash' | 'cash-to-card';
}

export const usePOSAPI = (
  onSuccess: (message: string, order?: Order) => void,
  onError: (message: string) => void,
  onDataUpdate: () => void
): POSAPIActions => {
  const apiService = ApiService.getInstance();
  const { activeSession } = usePinSessions();

  // Single code path: delegate to orders API (items mapping, sub_bills, happy_hour_discount_amount in orders.ts)
  const createOrder = useCallback(
    async (orderData: CreateOrderData) => {
      try {
        const attribution = getFloorOrderAttribution();
        const tableLabel =
          orderData.tableLabel !== undefined
            ? orderData.tableLabel
            : (attribution?.tableLabel ?? null);
        const pinActorToken = activeSession?.actor.token;
        if (tableLabel && !pinActorToken) {
          throw new Error('Session PIN requise pour une commande à table');
        }
        const created = await apiService.createOrder({
          totalAmount: orderData.totalAmount,
          taxAmount: orderData.totalTax,
          paymentMethod: orderData.paymentMethod,
          items: orderData.items,
          sub_bills: orderData.subBills?.map(bill => {
            const method = bill.payments[0]?.method || 'card';
            return {
              payment_method: method === 'split' ? 'card' : method,
              amount: bill.total,
            };
          }),
          tips: orderData.tips ?? 0,
          change: orderData.change ?? 0,
          notes: orderData.notes,
          waiter_user_id:
            orderData.waiterUserId !== undefined
              ? orderData.waiterUserId
              : (attribution?.waiterUserId ?? null),
          waiter_display_name:
            orderData.waiterDisplayName !== undefined
              ? orderData.waiterDisplayName
              : (attribution?.waiterDisplayName ?? null),
          table_label: tableLabel,
          pinActorToken,
        });
        onSuccess('Commande créée avec succès', created);
        onDataUpdate();
        return created;
      } catch (error: unknown) {
        const err = error as { response?: { data?: { error?: string } }; message?: string };
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          'Erreur lors de la création de la commande';
        onError(errorMessage);
        throw error;
      }
    },
    [apiService, onSuccess, onError, onDataUpdate, activeSession?.actor.token]
  );

  const processChange = useCallback(
    async (changeData: ChangeData) => {
      try {
        const response = await apiService.post<ChangeResponse>('/orders/payment/change', {
          amount: changeData.amount,
          direction: changeData.direction,
        });

        onSuccess('Changement de caisse effectué');
        onDataUpdate();
        return response.data;
      } catch (error: unknown) {
        const err = error as { response?: { data?: { error?: string } }; message?: string };
        const errorMessage =
          err.response?.data?.error || err.message || 'Erreur lors du changement de caisse';
        onError(errorMessage);
        throw error;
      }
    },
    [apiService, onSuccess, onError, onDataUpdate]
  );

  return {
    createOrder,
    processChange,
  };
};
